import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { NgTemplateOutlet } from '@angular/common';
import { FilterChipComponent } from '../../components/filter-chip/filter-chip.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { ProjectRoleService } from '../../services/project-role.service';
import { ObjectEntryDialogComponent } from './object-entry-dialog/object-entry-dialog.component';
import { ObjectRenameDialogComponent } from './object-rename-dialog/object-rename-dialog.component';
import { ObjectMappingCreateDialogComponent } from './object-mapping-create-dialog/object-mapping-create-dialog.component';
import { ObjectUsageListComponent } from './object-usage-list/object-usage-list.component';
import { ObjectAssignmentViewComponent } from './object-assignment-view/object-assignment-view.component';
import { assignmentRows, openReferenceRows } from './object-assignment.util';
import { VerwendungCardComponent } from '../../components/verwendung-card/verwendung-card.component';
import { VerwendungEintrag, VerwendungReiter, VerwendungStatus } from '../../models/verwendung.model';
import { serverMessage } from '../../shared/http/server-message';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { IconComponent } from '../../components/icon/icon.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { technologyKey } from './technology';
import { readLocator } from './locator-kinds';
import { ObjectMappingService } from '../../services/object-mapping.service';
import { ObjectCaptureService } from '../../services/object-capture.service';
import {
  ENTRY_STATES,
  EntryState,
  ObjectMappingEntryView,
  ObjectMappingLocator,
  ObjectMappingSummary,
  ObjectUsages,
  OpenReference,
  RenameResult,
  UsageOwner,
} from '../../models/object-mapping.model';
import { isRecordEngine } from '../../services/execution-job.service';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Ab so vielen Tagen steht das Datum statt „vor N Tagen“. */
const RELATIVE_DAYS_LIMIT = 30;

const STATE_COUNTS: Record<EntryState, keyof ObjectMappingSummary> = {
  FRAGILE: 'fragileCount',
  UNUSED: 'unusedCount',
  NEVER_HIT: 'neverHitCount',
  MISSING: 'missingCount',
};

interface StateChip {
  key: EntryState | 'ALL';
  state: EntryState | null;
  count: number | null;
  active: boolean;
}

interface LastHit {
  when: string;
  /** i18n-Schluessel des Tooltips, wenn der Treffer brüchig ist (spaeterer Kandidat oder Position). */
  warning: string | null;
}

interface Candidate {
  /** i18n-Schluessel der Art oder der Rohname einer unbekannten Strategie. */
  label: string;
  value: string;
  extras: string;
}

interface EntryRow {
  id: string;
  logicalName: string;
  typeName: string;
  area: string | null;
  candidates: Candidate[];
  lastHit: LastHit | null;
  missingSince: string | null;
  entry: ObjectMappingEntryView;
}

type LoadState = 'ok' | 'notFound' | 'error';
type HeadState = 'loading' | LoadState;

/** Zeilen des Skeletts, solange die erste Seite laedt. */
const SKELETON_ROWS = [1, 2, 3];

/** Offener Eintrag-Dialog; ohne `entry` wird angelegt, `presetName` kommt aus einer offenen Referenz. */
interface EntryDialogTarget {
  entry: ObjectMappingEntryView | null;
  presetName?: string;
}

/** Ansicht der Seite, im Query-Parameter `ansicht` (Bundle `:337`); ohne Parameter „Objekte“. */
type DetailView = 'objekte' | 'zuordnung';

interface ViewTab {
  id: DetailView;
  label: string;
  /** `null`, solange die Zahl nicht feststeht. */
  count: number | null;
  active: boolean;
}

/** Offener Umbenennen-Dialog; ohne `entryId` fuer die ganze Bibliothek. */
interface RenameTarget {
  entryId?: string;
  name: string;
}

/** Verwender-Dialog; ohne `entryId` fuer das ganze Testelement (Kopf). */
interface UsagesView {
  entryId: string | null;
  name: string;
  usages: ObjectUsages | null;
  error: boolean;
}

/**
 * Loeschen-Dialog nach Bundle (A6): Die Verwender laden beim Oeffnen. `usedBy` sind ihre Kennungen; `null`,
 * solange keine bekannt sind (auch wenn das Laden scheitert, dann fragt das erste DELETE ohne `confirm`).
 */
interface LibraryDeleteView {
  loading: boolean;
  usedBy: string | null;
  busy: boolean;
  error: string | null;
}

interface DeleteView extends LibraryDeleteView {
  row: EntryRow;
}

/**
 * Detailseite eines Testelements mit seinen Objekten, seitenweise nachgeladen (NEX-1276, NEX-2450).
 * Design `page-objektbibliothek.jsx:347-445`, Karte „Verwendung“ `:446-455` (NEX-2453), Dialoge `:460-480`
 * (NEX-2451), Umschalter `:369-377` mit der Ansicht „Zuordnung zu Testfällen“ (NEX-2454); Kopf und Zaehler
 * kommen aus `GET …/{mappingId}`.
 */
@Component({
  selector: 'app-object-mapping-detail',
  imports: [
    TranslatePipe,
    RouterLink,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatTooltip,
    NgTemplateOutlet,
    FilterChipComponent,
    StatusPillComponent,
    ProjectNavComponent,
    IconComponent,
    SearchBarComponent,
    ConfirmDialogComponent,
    ObjectRenameDialogComponent,
    ObjectEntryDialogComponent,
    ObjectMappingCreateDialogComponent,
    ObjectUsageListComponent,
    ObjectAssignmentViewComponent,
    VerwendungCardComponent,
  ],
  templateUrl: './object-mapping-detail.component.html',
  styleUrl: './object-mapping-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectMappingDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(ObjectMappingService);
  private readonly captures = inject(ObjectCaptureService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  readonly isReadOnly = inject(ProjectRoleService).isReadOnly;

  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly mappingId: string = this.route.snapshot.params['mappingId'];

  readonly library = signal<ObjectMappingSummary | null>(null);
  readonly searchTerm = signal('');
  readonly entryState = signal<EntryState | null>(null);
  readonly loading = signal(true);
  readonly loadingMore = signal(false);
  readonly state = signal<LoadState>('ok');
  /** Zustand des Einzelabrufs; nach dem ersten Erfolg bleibt ein spaeter Fehler unsichtbar. */
  readonly headState = signal<HeadState>('loading');
  /** Ein unbekanntes Testelement meldet der Kopf oder die erste Seite der Eintraege. */
  readonly pageState = computed<HeadState>(() =>
    this.headState() === 'notFound' || this.state() === 'notFound' ? 'notFound' : this.headState(),
  );
  readonly overviewLink = ['/projects', this.projectId, 'object-mappings'];
  readonly skeletonRows = SKELETON_ROWS;
  readonly technologyOf = technologyKey;
  readonly moreError = signal(false);
  readonly last = signal(true);
  readonly total = signal(0);
  readonly expanded = signal<ReadonlySet<string>>(new Set());

  readonly renameTarget = signal<RenameTarget | null>(null);
  readonly entryDialog = signal<EntryDialogTarget | null>(null);
  readonly usagesView = signal<UsagesView | null>(null);
  readonly deleteView = signal<DeleteView | null>(null);
  readonly libraryDeleteView = signal<LibraryDeleteView | null>(null);
  readonly notice = signal<string | null>(null);
  /** Offene Aufnahmen; 0 auch, wenn sie nicht geladen werden konnten, denn der Hinweis ist nur ein Wegweiser. */
  readonly pendingCaptures = signal(0);
  readonly pendingCapturesKey = computed(() =>
    this.pendingCaptures() === 1 ? 'objectMappings.detail.pendingCapturesOne' : 'objectMappings.detail.pendingCaptures',
  );
  readonly reviewLink = ['/projects', this.projectId, 'object-mappings', this.mappingId, 'review'];

  readonly recordDialogOpen = signal(false);

  /** Karte „Verwendung“ (NEX-2453): Testfaelle und Testmodule des ganzen Testelements; freie Schritte zeigt sie nicht. */
  readonly usageStatus = signal<VerwendungStatus>('laedt');
  private readonly usage = signal<ObjectUsages | null>(null);
  private usageRequest: Subscription | null = null;
  private readonly languageChange = toSignal(this.translate.onLangChange, { initialValue: null });
  readonly usageTabs = computed<VerwendungReiter[]>(() => {
    this.languageChange();
    const usage = this.usage();
    const text = (key: string): string => this.translate.instant(`objectMappings.detail.usage.${key}`);
    return [
      {
        id: 'tc',
        label: text('testCases'),
        icon: 'folder',
        leerText: text('testCasesEmpty'),
        suchPlatzhalter: text('testCasesSearch'),
        eintraege: this.usageEntries(usage?.testCases ?? [], 'test-cases'),
      },
      {
        id: 'tm',
        label: text('modules'),
        // Material-Gegenstueck des Bundle-Icons `puzzle`; die Karte rendert nur Material-Icons.
        icon: 'extension',
        leerText: text('modulesEmpty'),
        suchPlatzhalter: text('modulesSearch'),
        eintraege: this.usageEntries(usage?.modules ?? [], 'test-modules'),
      },
    ];
  });

  /** Offene Referenzen (NEX-2454); laden beim Oeffnen parallel zu den Verwendern (L4). */
  readonly openReferencesStatus = signal<VerwendungStatus>('laedt');
  private readonly openReferences = signal<OpenReference[] | null>(null);
  private openReferencesRequest: Subscription | null = null;
  /** Die Zuordnung braucht beides; scheitert eines, zeigt nur sie den Fehler. */
  readonly assignmentStatus = computed<VerwendungStatus>(() => {
    const states = [this.usageStatus(), this.openReferencesStatus()];
    if (states.includes('fehler')) return 'fehler';
    return states.includes('laedt') ? 'laedt' : 'bereit';
  });
  readonly assignmentRows = computed(() => assignmentRows(this.usage(), this.library()?.name ?? ''));
  readonly openRows = computed(() => openReferenceRows(this.openReferences()));

  readonly view = signal<DetailView>('objekte');
  readonly viewTabs = computed<ViewTab[]>(() => {
    const view = this.view();
    const assignmentCount =
      this.assignmentStatus() === 'bereit' ? this.assignmentRows().length + this.openRows().length : null;
    return [
      {
        id: 'objekte',
        label: 'objectMappings.detail.view.objects',
        count: this.library()?.entryCount ?? null,
        active: view === 'objekte',
      },
      {
        id: 'zuordnung',
        label: 'objectMappings.detail.view.assignment',
        count: assignmentCount,
        active: view === 'zuordnung',
      },
    ];
  });

  readonly fieldLengths = FIELD_LENGTHS;
  readonly tags = computed(() => this.library()?.tags ?? []);
  readonly tagError = signal<string | null>(null);

  /**
   * Aufnehmen gibt es fuer Browser-, Windows- und SAP-Bibliotheken (NEX-1719, NEX-2066); solange der
   * Kopf laedt, ist offen, welche Engine.
   */
  readonly recordable = computed(() => isRecordEngine(this.library()?.engineId));

  private readonly entries = signal<ObjectMappingEntryView[]>([]);
  readonly rows = computed<EntryRow[]>(() => {
    const lang = this.translate.getCurrentLang() || 'de';
    const now = new Date();
    const engineId = this.library()?.engineId;
    return this.entries().map((entry) => toRow(entry, engineId, lang, now));
  });
  /** Syntax-Box: das erste Objekt der angezeigten Liste, sonst das Beispiel des Bundles. */
  readonly firstObjectName = computed(() => this.rows()[0]?.logicalName ?? 'bOk');
  readonly stateChips = computed<StateChip[]>(() => {
    const library = this.library();
    const active = this.entryState();
    const all: StateChip = { key: 'ALL', state: null, count: library?.entryCount ?? null, active: active === null };
    return [
      all,
      ...ENTRY_STATES.map((state) => ({
        key: state,
        state,
        count: (library?.[STATE_COUNTS[state]] as number | undefined) ?? null,
        active: active === state,
      })),
    ];
  });
  readonly canLoadMore = computed(() => !this.loading() && this.state() === 'ok' && !this.last());

  private readonly searchInput = new Subject<string>();
  private query = '';
  private nextPage = 0;
  private request: Subscription | null = null;
  /** Nach dem Speichern aufklappen, sobald die erste Seite ihn enthaelt. */
  private expandAfterLoad: string | null = null;

  constructor() {
    this.searchInput
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        map((term) => term.trim()),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((q) => this.startSearch(q));
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
  }

  ngOnInit(): void {
    this.loadLibrary();
    this.loadUsage();
    this.loadOpenReferences();
    this.route.queryParamMap
      .pipe(
        map((params) => (params.get('ansicht') === 'zuordnung' ? 'zuordnung' : 'objekte')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((view: DetailView) => this.view.set(view));
    this.captures
      .list(this.projectId, this.mappingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (list) => this.pendingCaptures.set(list.length), error: () => this.pendingCaptures.set(0) });
    // Abonniert statt aus dem Snapshot gelesen: Jede Chip-Wahl legt einen Verlaufseintrag an,
    // und nach „Zurueck“ soll der Filter der URL folgen.
    let first = true;
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('state')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((requested) => {
        const state = ENTRY_STATES.find((s) => s === requested) ?? null;
        if (requested !== null && !state) this.navigateToState(null, true);
        if (!first && state === this.entryState()) return;
        first = false;
        this.entryState.set(state);
        this.startSearch(this.query);
      });
  }

  /** Wie der Filter: jede Wahl legt einen Verlaufseintrag an, „Objekte“ entfernt den Parameter. */
  selectView(view: DetailView): void {
    if (view === this.view()) return;
    this.view.set(view);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ansicht: view === 'zuordnung' ? view : null },
      queryParamsHandling: 'merge',
    });
  }

  /** Pfeiltasten wechseln den Reiter (WAI-ARIA Tabs); der Fokus folgt. */
  onViewKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next: DetailView = this.view() === 'objekte' ? 'zuordnung' : 'objekte';
    this.selectView(next);
    const tabs = (event.currentTarget as HTMLElement).parentElement;
    tabs?.querySelector<HTMLElement>(`#om-view-tab-${next}`)?.focus();
  }

  /** „＋ Objekt anlegen“ aus der Warnbox (AK 11): Eintrag-Dialog mit dem Namen der Referenz. */
  createFromReference(name: string): void {
    this.notice.set(null);
    this.entryDialog.set({ entry: null, presetName: name });
  }

  /** Laedt nur, was gescheitert ist. */
  retryAssignment(): void {
    if (this.usageStatus() === 'fehler') this.loadUsage();
    if (this.openReferencesStatus() === 'fehler') this.loadOpenReferences();
  }

  selectState(state: EntryState | null): void {
    if (state === this.entryState()) return;
    this.entryState.set(state);
    this.navigateToState(state, false);
    this.startSearch(this.query);
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.searchInput.next(term);
  }

  retry(): void {
    this.startSearch(this.query);
  }

  retryPage(): void {
    this.headState.set('loading');
    this.loadLibrary();
    this.loadUsage();
    this.loadOpenReferences();
    this.startSearch(this.query);
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.loadingMore()) return;
    this.loadingMore.set(true);
    this.moreError.set(false);
    this.fetch();
  }

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  toggle(id: string): void {
    const next = new Set(this.expanded());
    if (!next.delete(id)) next.add(id);
    this.expanded.set(next);
  }

  openRename(row?: EntryRow): void {
    this.notice.set(null);
    this.renameTarget.set(
      row ? { entryId: row.id, name: row.logicalName } : { name: this.library()?.name ?? this.mappingId },
    );
  }

  openEntryDialog(row?: EntryRow): void {
    this.notice.set(null);
    this.entryDialog.set({ entry: row?.entry ?? null });
  }

  onEntrySaved(saved: ObjectMappingEntryView): void {
    this.entryDialog.set(null);
    this.notice.set(this.translate.instant('objectMappings.detail.saved', { name: saved.logicalName }));
    this.expandAfterLoad = saved.id;
    this.loadLibrary();
    this.loadUsage();
    this.loadOpenReferences();
    this.startSearch(this.query);
  }

  onRenamed(result: RenameResult): void {
    this.renameTarget.set(null);
    this.notice.set(
      this.translate.instant('objectMappings.detail.renamed', {
        oldName: result.oldName,
        newName: result.newName,
        count: result.occurrenceCount,
      }),
    );
    this.loadLibrary();
    this.loadUsage();
    this.loadOpenReferences();
    this.startSearch(this.query);
  }

  /** Die Schreibung bleibt, wie getippt: importierte Tags wie `attr:Status=Freigegeben` brauchen sie. */
  addTag(input: HTMLInputElement): void {
    const value = input.value.trim();
    input.value = '';
    if (!value || this.tags().includes(value)) return;
    this.saveTags([...this.tags(), value]);
  }

  removeTag(tag: string): void {
    this.saveTags(this.tags().filter((t) => t !== tag));
  }

  /** Zeigt die neuen Tags gleich; scheitert das Speichern, kommt der alte Stand zurueck. */
  private saveTags(tags: string[]): void {
    const before = this.library();
    if (!before) return;
    this.tagError.set(null);
    this.library.set({ ...before, tags });
    this.service
      .updateTags(this.projectId, this.mappingId, tags)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => this.library.set(saved),
        error: (err: unknown) => {
          this.library.update((current) => (current ? { ...current, tags: before.tags ?? [] } : current));
          this.tagError.set(serverMessage(err) ?? this.translate.instant('objectMappings.detail.tags.saveError'));
        },
      });
  }

  /** Ohne Zeile die Verwender des ganzen Testelements (Kopf, A7). */
  showUsages(row?: EntryRow): void {
    const libraryName = this.library()?.name ?? this.mappingId;
    const entryId = row?.id ?? null;
    this.usagesView.set({
      entryId,
      name: row ? `${libraryName}.${row.logicalName}` : libraryName,
      usages: null,
      error: false,
    });
    const forThisView = (change: Partial<UsagesView>) =>
      this.usagesView.update((view) => (view?.entryId === entryId ? { ...view, ...change } : view));
    this.service
      .usages(this.projectId, this.mappingId, row?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usages) => forThisView({ usages }),
        error: () => forThisView({ error: true }),
      });
  }

  deleteEntry(row: EntryRow): void {
    this.notice.set(null);
    this.deleteView.set({ row, ...OPEN_DELETE });
    const forThisRow = (change: Partial<DeleteView>) =>
      this.deleteView.update((view) => (view?.row.id === row.id ? { ...view, ...change } : view));
    this.service
      .usages(this.projectId, this.mappingId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usages) => forThisRow({ loading: false, usedBy: usedBy(usages) }),
        error: () => forThisRow({ loading: false }),
      });
  }

  /**
   * Mit bekannten Verwendern gleich `confirm=true`. Sonst ohne: Meldet das BE doch Verwender (409),
   * wechselt der Dialog in den Zustand mit Verwendern und fragt noch einmal.
   */
  confirmDelete(): void {
    const view = this.deleteView();
    if (!view || view.busy || view.loading) return;
    this.deleteView.set({ ...view, busy: true, error: null });
    this.service
      .deleteEntry(this.projectId, this.mappingId, view.row.id, view.usedBy !== null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleteView.set(null);
          this.afterDelete(view.row);
        },
        error: (err: unknown) =>
          this.deleteView.update(
            (current) => current && this.afterFailedDelete(current, err, 'objectMappings.detail.deleteError'),
          ),
      });
  }

  deleteLibrary(): void {
    this.notice.set(null);
    this.libraryDeleteView.set({ ...OPEN_DELETE });
    this.service
      .usages(this.projectId, this.mappingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usages) =>
          this.libraryDeleteView.update((view) => view && { ...view, loading: false, usedBy: usedBy(usages) }),
        error: () => this.libraryDeleteView.update((view) => view && { ...view, loading: false }),
      });
  }

  /** Wie `confirmDelete`. */
  confirmLibraryDelete(): void {
    const view = this.libraryDeleteView();
    if (!view || view.busy || view.loading) return;
    this.libraryDeleteView.set({ ...view, busy: true, error: null });
    this.service
      .deleteMapping(this.projectId, this.mappingId, view.usedBy !== null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.libraryDeleteView.set(null);
          this.router.navigate(['/projects', this.projectId, 'object-mappings']);
        },
        error: (err: unknown) =>
          this.libraryDeleteView.update(
            (current) => current && this.afterFailedDelete(current, err, 'objectMappings.detail.deleteLibrary.error'),
          ),
      });
  }

  private afterDelete(row: EntryRow): void {
    this.notice.set(this.translate.instant('objectMappings.detail.deleted', { name: row.logicalName }));
    this.loadLibrary();
    this.loadUsage();
    this.loadOpenReferences();
    this.startSearch(this.query);
  }

  /** 409 mit Verwendern auf eine Anfrage ohne `confirm` fuehrt zur zweiten Stufe, alles andere ist ein Fehler. */
  private afterFailedDelete<V extends LibraryDeleteView>(view: V, err: unknown, errorKey: string): V {
    const body = view.usedBy === null && err instanceof HttpErrorResponse && err.status === 409 ? err.error : null;
    const users = isUsages(body) ? usedBy(body) : null;
    return users !== null
      ? { ...view, usedBy: users, busy: false }
      : { ...view, busy: false, error: serverMessage(err) ?? this.translate.instant(errorKey) };
  }

  private startSearch(q: string): void {
    this.query = q;
    this.nextPage = 0;
    this.entries.set([]);
    this.expanded.set(new Set());
    this.loading.set(true);
    this.loadingMore.set(false);
    this.moreError.set(false);
    this.state.set('ok');
    this.fetch();
  }

  private fetch(): void {
    this.request?.unsubscribe();
    this.request = this.service
      .entries(this.projectId, this.mappingId, this.query, this.nextPage, PAGE_SIZE, this.entryState())
      .subscribe({
        next: (page) => {
          this.entries.update((loaded) => [...loaded, ...page.content]);
          const wanted = this.expandAfterLoad;
          this.expandAfterLoad = null;
          if (wanted && page.content.some((e) => e.id === wanted)) this.expanded.set(new Set([wanted]));
          this.nextPage = page.number + 1;
          this.last.set(page.last);
          this.total.set(page.totalElements);
          this.loading.set(false);
          this.loadingMore.set(false);
        },
        error: (err: unknown) => {
          this.expandAfterLoad = null;
          if (this.loadingMore()) {
            this.moreError.set(true);
          } else {
            this.state.set(err instanceof HttpErrorResponse && err.status === 404 ? 'notFound' : 'error');
          }
          this.loading.set(false);
          this.loadingMore.set(false);
        },
      });
  }

  /** Ohne `replaceUrl` bleibt der Filter in der Verlaufsliste; ein ungueltiger Wert nicht. */
  private navigateToState(state: EntryState | null, replaceUrl: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { state },
      queryParamsHandling: 'merge',
      ...(replaceUrl ? { replaceUrl } : {}),
    });
  }

  /**
   * Auch nach eigenem Speichern und Loeschen, denn das BE bindet Verweise dabei neu. Geladene Daten bleiben bis zur
   * Antwort stehen; eine neue Anfrage verwirft die vorige, damit keine veraltete Antwort gewinnt.
   */
  loadUsage(): void {
    this.usageRequest?.unsubscribe();
    if (this.usageStatus() !== 'bereit') this.usageStatus.set('laedt');
    this.usageRequest = this.service
      .usages(this.projectId, this.mappingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usage) => {
          this.usage.set(usage);
          this.usageStatus.set('bereit');
        },
        error: () => this.usageStatus.set('fehler'),
      });
  }

  /** Wie `loadUsage`: Geladenes bleibt bis zur Antwort stehen, eine neue Anfrage verwirft die vorige. */
  loadOpenReferences(): void {
    this.openReferencesRequest?.unsubscribe();
    if (this.openReferencesStatus() !== 'bereit') this.openReferencesStatus.set('laedt');
    this.openReferencesRequest = this.service
      .openReferences(this.projectId, this.mappingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (references) => {
          this.openReferences.set(references);
          this.openReferencesStatus.set('bereit');
        },
        error: () => this.openReferencesStatus.set('fehler'),
      });
  }

  private usageEntries(owners: UsageOwner[], target: 'test-cases' | 'test-modules'): VerwendungEintrag[] {
    return owners.map((owner) => ({
      id: owner.id,
      key: owner.key,
      titel: owner.title,
      ziel: { commands: ['/projects', this.projectId, target, owner.id, 'edit'] },
    }));
  }

  private loadLibrary(): void {
    this.service
      .getDetail(this.projectId, this.mappingId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (library) => {
          this.library.set(library);
          this.headState.set('ok');
        },
        // Nach Speichern, Umbenennen und Loeschen bleibt bei einem Fehler der bekannte Stand stehen.
        error: (err: unknown) => {
          if (this.library()) return;
          this.headState.set(err instanceof HttpErrorResponse && err.status === 404 ? 'notFound' : 'error');
        },
      });
  }
}

const OPEN_DELETE: LibraryDeleteView = { loading: true, usedBy: null, busy: false, error: null };

/** Kennungen der Testfaelle, dann der Testmodule, dann der freien Schritte, ohne Doppelte (A6); ohne Verwender `null`. */
function usedBy(usages: ObjectUsages): string | null {
  const ids = new Set([
    ...usages.testCases.map((owner) => owner.key),
    ...usages.modules.map((owner) => owner.key),
    ...usages.unlinkedSteps.map((step) => step.stepKey),
  ]);
  return ids.size > 0 ? [...ids].join(', ') : null;
}

function isUsages(body: unknown): body is ObjectUsages {
  return !!body && typeof body === 'object' && Array.isArray((body as ObjectUsages).testCases);
}

function toRow(entry: ObjectMappingEntryView, engineId: string | undefined, lang: string, now: Date): EntryRow {
  return {
    id: entry.id,
    logicalName: entry.logicalName,
    typeName: entry.typeName ?? '',
    area: entry.area?.trim() || null,
    candidates: (entry.locators ?? []).map((locator) => toCandidate(locator, engineId)),
    lastHit: entry.lastHitAt ? toLastHit(entry, entry.lastHitAt, lang, now) : null,
    missingSince: entry.missingSince ? formatDayMonth(new Date(entry.missingSince)) : null,
    entry,
  };
}

/** Brüchig wie `EntryState.FRAGILE` im BE: ueber einen spaeteren Kandidaten oder nur die Position getroffen. */
function toLastHit(entry: ObjectMappingEntryView, at: string, lang: string, now: Date): LastHit {
  let warning: string | null = null;
  if ((entry.lastHitIndex ?? 0) > 0) warning = 'objectMappings.state.laterCandidate';
  else if (entry.lastHitKind === 'POSITION') warning = 'objectMappings.state.positionOnly';
  return { when: relativeDay(new Date(at), lang, now), warning };
}

/** Nach Kalendertagen, nicht nach 24-Stunden-Spannen: 23:55 gestern ist „gestern“. */
function relativeDay(then: Date, lang: string, now: Date): string {
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY_MS);
  if (days >= RELATIVE_DAYS_LIMIT) return formatDay(then);
  return new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }).format(-days, 'day');
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDay(date: Date): string {
  return `${formatDayMonth(date)}${date.getFullYear()}`;
}

/** „verschwunden seit 12.09.“ wie im Bundle, ohne Jahr. */
function formatDayMonth(date: Date): string {
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.`;
}

/** Der Name einer Rolle steckt schon im Wert (`button[name='Anmelden']`); weitere Felder bleiben sichtbar. */
function toCandidate(locator: ObjectMappingLocator, engineId: string | undefined): Candidate {
  const shownInValue = new Set(['strategy', 'value', ...(locator['strategy'] === 'role' ? ['name'] : [])]);
  const { label, value } = readLocator(locator, engineId);
  return {
    label,
    value,
    extras: Object.entries(locator)
      .filter(([key]) => !shownInValue.has(key))
      .map(([key, val]) => `${key}=${val}`)
      .join(' · '),
  };
}
